import {
  Box, Button, Flex, Input, Text, VStack,
  InputGroup, InputRightElement, IconButton,
  useToast, FormControl, FormErrorMessage
} from '@chakra-ui/react';
import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  registerCompany,
  deleteCompany,
  registerAdmin
} from '../services/api';

const Register = () => {
  const navigate = useNavigate();
  const toast = useToast();

  const [step, setStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false);
  const [companyId, setCompanyId] = useState(null);

  const [companyData, setCompanyData] = useState({
    company_name: '', address: '', city: '', postal_code: '', province: '',
    region: '', country: '', email: '', phone: '', website_url: '',
    pec: '', sdi_code: '', tax_code: '', vat_number: '', customer_role_id: 2
  });

  const [userData, setUserData] = useState({
    name: '', email: '', phone: '', password: '', password_confirmation: ''
  });

  const [errors, setErrors] = useState({});

  const validateFields = (fields) => {
    const newErrors = {};
    for (const key in fields) {
      if (!fields[key]) newErrors[key] = 'Campo obbligatorio';
    }
    return newErrors;
  };

  const handleCompanyChange = (e) => {
    setCompanyData({ ...companyData, [e.target.name]: e.target.value });
  };

  const handleUserChange = (e) => {
    setUserData({ ...userData, [e.target.name]: e.target.value });
  };

  const handleCompanySubmit = async () => {
    const validationErrors = validateFields({
      email: companyData.email,
      phone: companyData.phone,
      website_url: companyData.website_url,
      pec: companyData.pec,
      sdi_code: companyData.sdi_code,
      tax_code: companyData.tax_code,
      vat_number: companyData.vat_number
    });

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      toast({
        title: 'Campi mancanti',
        description: 'Compila tutti i campi richiesti.',
        status: 'error', duration: 3000, isClosable: true
      });
      return;
    }

    try {
      const res = await registerCompany(companyData);
      setCompanyId(res.data.id);
      toast({
        title: 'Azienda registrata con successo',
        status: 'success', duration: 2000, isClosable: true
      });
      setStep(3);
    } catch (error) {
      const msg = error.response?.data?.message || 'Errore in fase di registrazione.';
      const serverErrors = error.response?.data?.errors || {};
      setErrors(serverErrors);

      toast({
        title: 'Errore registrazione azienda',
        description: msg,
        status: 'error', duration: 4000, isClosable: true
      });
    }
  };

  const handleRegister = async () => {
    const { phone, ...requiredUserFields } = userData;
    const validationErrors = validateFields(requiredUserFields);

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      toast({
        title: 'Campi mancanti',
        description: 'Compila tutti i campi richiesti.',
        status: 'error', duration: 3000, isClosable: true
      });
      return;
    }

    if (userData.password !== userData.password_confirmation) {
      toast({
        title: 'Errore',
        description: 'Le password non coincidono.',
        status: 'error', duration: 3000, isClosable: true
      });
      return;
    }

    try {
      const res = await registerAdmin(companyId, userData);
      const { user, token } = res.data;

      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('token', token);

      toast({ title: 'Registrazione completata!', status: 'success', duration: 3000, isClosable: true });
      navigate('/create-quote');
    } catch (error) {
      console.error(error);
      toast({
        title: 'Errore registrazione utente',
        description: 'Rollback e registrazione fallita.',
        status: 'error', duration: 3000, isClosable: true
      });

      if (companyId) {
        try {
          await deleteCompany(companyId);
        } catch (rollbackError) {
          console.error('Errore rollback azienda:', rollbackError);
        }
      }
    }
  };

  const nextStep = () => {
    const validationErrors = validateFields({
      company_name: companyData.company_name,
      address: companyData.address,
      city: companyData.city,
      postal_code: companyData.postal_code,
      province: companyData.province,
      region: companyData.region,
      country: companyData.country
    });

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      toast({
        title: 'Campi mancanti',
        description: 'Compila tutti i campi richiesti.',
        status: 'error', duration: 3000, isClosable: true
      });
    } else {
      setStep(2);
    }
  };

  return (
    <Flex justify="center" align="center" height="100vh">
      <Box bg="white" p={8} borderRadius="md" boxShadow="lg" minW="350px">
        {step === 1 && (
          <>
            <Text fontSize="2xl" mb={4}>Dati Aziendali - Parte 1</Text>
            <VStack spacing={3}>
              {['company_name', 'address', 'city', 'postal_code', 'province', 'region', 'country'].map((field) => (
                <FormControl key={field} isInvalid={!!errors[field]}>
                  <Input placeholder={field.toUpperCase()} name={field} value={companyData[field]} onChange={handleCompanyChange} />
                  <FormErrorMessage>{errors[field]}</FormErrorMessage>
                </FormControl>
              ))}
            </VStack>
            <Flex justify="space-between" mt={6}>
              <Button onClick={() => navigate('/')} variant="link">Torna al login</Button>
              <Button colorScheme="teal" onClick={nextStep}>Avanti</Button>
            </Flex>
          </>
        )}

        {step === 2 && (
          <>
            <Text fontSize="2xl" mb={4}>Dati Aziendali - Parte 2</Text>
            <VStack spacing={3}>
              {['email', 'phone', 'website_url', 'pec', 'sdi_code', 'tax_code', 'vat_number'].map((field) => (
                <FormControl key={field} isInvalid={!!errors[field]}>
                  <Input placeholder={field.toUpperCase()} name={field} value={companyData[field]} onChange={handleCompanyChange} />
                  <FormErrorMessage>{errors[field]}</FormErrorMessage>
                </FormControl>
              ))}
            </VStack>
            <Flex justify="space-between" mt={6}>
              <Button onClick={() => setStep(1)}>Indietro</Button>
              <Button colorScheme="teal" onClick={handleCompanySubmit}>Avanti</Button>
            </Flex>
          </>
        )}

        {step === 3 && (
          <>
            <Text fontSize="2xl" mb={4}>Dati Utente</Text>
            <VStack spacing={3}>
              {['name', 'email', 'phone'].map((field) => (
                <FormControl key={field} isInvalid={!!errors[field]}>
                  <Input placeholder={field.toUpperCase()} name={field} value={userData[field]} onChange={handleUserChange} />
                  <FormErrorMessage>{errors[field]}</FormErrorMessage>
                </FormControl>
              ))}
              <FormControl isInvalid={!!errors.password}>
                <InputGroup>
                  <Input
                    placeholder="Password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={userData.password}
                    onChange={handleUserChange}
                  />
                  <InputRightElement>
                    <IconButton
                      variant="ghost"
                      icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label="Toggle password"
                    />
                  </InputRightElement>
                </InputGroup>
                <FormErrorMessage>{errors.password}</FormErrorMessage>
              </FormControl>
              <FormControl isInvalid={!!errors.password_confirmation}>
                <InputGroup>
                  <Input
                    placeholder="Conferma Password"
                    name="password_confirmation"
                    type={showPasswordConfirmation ? 'text' : 'password'}
                    value={userData.password_confirmation}
                    onChange={handleUserChange}
                  />
                  <InputRightElement>
                    <IconButton
                      variant="ghost"
                      icon={showPasswordConfirmation ? <ViewOffIcon /> : <ViewIcon />}
                      onClick={() => setShowPasswordConfirmation(!showPasswordConfirmation)}
                      aria-label="Toggle confirm password"
                    />
                  </InputRightElement>
                </InputGroup>
                <FormErrorMessage>{errors.password_confirmation}</FormErrorMessage>
              </FormControl>
            </VStack>
            <Flex justify="space-between" mt={6}>
              <Button onClick={() => setStep(2)}>Indietro</Button>
              <Button colorScheme="teal" onClick={handleRegister}>Registrati</Button>
            </Flex>
          </>
        )}
      </Box>
    </Flex>
  );
};

export default Register;
